import {tagAnatomy as parts} from '@chakra-ui/anatomy';
import {createMultiStyleConfigHelpers, cssVar, defineCssVars, defineStyle} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {semantic as dark} from '../../design-tokens/dark';
import {core, semantic} from '../../design-tokens/light';
import {getCoreValueAsThemeToken} from '../types';

import {badgeTheme, badgeVars} from './Badge';

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(parts.keys);

const {
  bg: $bg,
  border: $border,
  borderColor: $borderColor,
  paddingX: $paddingX,
  radii: $radii,
  color: $color,
  lineHeight: $lineHeight,
  height: $height,
  fontWeight: $fontWeight,
} = defineCssVars(`tag`, [
  `bg`,
  `border`,
  `borderColor`,
  `paddingX`,
  `radii`,
  `color`,
  `lineHeight`,
  `height`,
  `fontWeight`,
]);

const $shadow = cssVar(`tag-shadow`);
const $minW = cssVar(`tag-min-width`);
const $fontSize = cssVar(`tag-font-size`);

const baseStyleContainer = defineStyle(props => ({
  [$color.variable]: props.colorScheme
    ? `colors.${props.colorScheme}.700`
    : mode(
        getCoreValueAsThemeToken(semantic, `colors.text.secondary`),
        getCoreValueAsThemeToken(semantic, `colors.text.secondary-inverse`),
      )(props),
  [$bg.variable]: props.colorScheme ? `colors.${props.colorScheme}.100` : `colors.obsidian.200`,
  [$shadow.variable]: badgeVars.shadow.reference,
  [$paddingX.variable]: `space.${getCoreValueAsThemeToken(core, `space.space-150`)}`,
  [$radii.variable]: `radii.${getCoreValueAsThemeToken(core, `radii.radius-1000`)}`,
  [$fontSize.variable]: `fontSizes.sm`,
  [$lineHeight.variable]: `2em`,
  [$fontWeight.variable]: `normal`,
  outline: 0,

  color: $color.reference,
  bg: $bg.reference,
  boxShadow: $shadow.reference,
  height: $height.reference,
  minW: $minW.reference,
  fontSize: $fontSize.reference,
  fontWeight: $fontWeight.reference,
  borderRadius: $radii.reference,
  lineHeight: $lineHeight.reference,
  px: $paddingX.reference,

  _focusVisible: {
    [$shadow.variable]: `shadows.outline`,
  },
}));

const baseStyleLabel = defineStyle({
  lineHeight: 1.2,
  overflow: `visible`,
});

const baseStyleCloseButton = defineStyle({
  fontSize: `lg`,
  w: `5`,
  h: `5`,
  transitionProperty: `common`,
  transitionDuration: `normal`,
  borderRadius: `full`,
  marginStart: `1.5`,
  marginEnd: `-1`,
  opacity: 0.5,
  _disabled: {
    opacity: 0.4,
  },
  _focusVisible: {
    boxShadow: `outline`,
    bg: `rgba(0, 0, 0, 0.14)`,
  },
  _hover: {
    opacity: 0.8,
  },
  _active: {
    opacity: 1,
  },
});

const baseStyle = definePartsStyle(props => ({
  container: baseStyleContainer(props),
  label: baseStyleLabel,
  closeButton: baseStyleCloseButton,
}));

const sizes = {
  xxs: definePartsStyle({
    container: {
      [$fontSize.variable]: `fontSizes.sm`,
      [$height.variable]: `1rem`,
      [$paddingX.variable]: `space.8`,
    },
  }),
  xs: definePartsStyle({
    container: {
      [$fontSize.variable]: `fontSizes.sm`,
      [$height.variable]: `1.5rem`,
      [$paddingX.variable]: `8px`,
    },
  }),
  sm: definePartsStyle({
    container: {
      [$fontSize.variable]: `fontSizes.sm`,
      [$height.variable]: `2rem`,
    },
    closeButton: {
      marginEnd: `-2px`,
      marginStart: `0.35rem`,
    },
  }),
  md: definePartsStyle({
    container: {
      [$height.variable]: `2.5rem`,
      [$minW.variable]: `sizes.6`,
      [$fontSize.variable]: `fontSizes.sm`,
    },
  }),
  lg: definePartsStyle({
    container: {
      [$minW.variable]: `sizes.8`,
      [$fontSize.variable]: `fontSizes.md`,
      [$paddingX.variable]: `space.3`,
    },
  }),
};

const variants = {
  secondary: definePartsStyle(props => {
    const {colorScheme: c} = props;

    const color = mode(`${c}.700`, `${c}.300`)(props);

    return {
      container: {
        [$bg.variable]: `colors.background.primary`,
        [$border.variable]: `1px solid`,
        [$borderColor.variable]: `colors.${mode(
          semantic.colors.stroke.secondary,
          dark.colors.stroke.secondary,
        )(props)}`,
        border: $border.reference,
        color: c ? color : mode(semantic.colors.text.secondary, dark.colors.text.secondary)(props),
        borderColor: $borderColor.reference,
      },
    };
  }),
  subtle: definePartsStyle(props => ({
    container: badgeTheme.variants?.subtle(props),
  })),
  solid: definePartsStyle(props => ({
    container: badgeTheme.variants?.solid(props),
  })),
  outline: definePartsStyle(props => ({
    container: badgeTheme.variants?.outline(props),
  })),
};

export const tagTheme = defineMultiStyleConfig({
  variants,
  baseStyle,
  sizes,
  defaultProps: {
    size: `md`,
    variant: `outline`,
  },
});
